import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Modal,
  Button,
  Typography,
  InputAdornment,
  Tooltip,
  useTheme,
} from "@mui/material";
import ExpandIcon from "@mui/icons-material/OpenInFull";
import CollapseIcon from "@mui/icons-material/CloseFullscreen";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import ComposeAttachMenu from "./ComposeAttachMenu";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FilePreview from "./FilePreview";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";

/** Extrai assunto do último e-mail recebido (emailOriginalTags ou texto) para Re: */
function getDefaultReplySubject(messages = []) {
  const list = Array.isArray(messages) ? messages : [];
  for (let i = 0; i < list.length; i++) {
    const msg = list[i];
    const tags = msg?.emailOriginalTags ?? msg?.emailOriginal ?? "";
    const str = typeof tags === "string" ? tags : "";
    const m = str.match(/\bSubject:\s*([^\n]+)/i);
    if (m && m[1]) return m[1].trim();
  }
  return "";
}

const MessageEmailChannel = ({
  message,
  setMessage,
  channelType,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  isMobile,
  isExpanded,
  setIsExpanded,
  file,
  files = [],
  handleRemoveFile,
  selectedChannel,
  setSelectedChannel,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  /** E-mail do destinatário da solicitação (pré-preenche "Para") */
  recipientEmail = "",
  /** Mensagens da conversa para extrair assunto em respostas (Re:) */
  messages = [],
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const inputBorderRadius = 16;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
  });
  const canUseAiSuggestion =
    !!onGenerateSuggestion && channelType !== "Xpro" && hasHistory;
  const aiDisabled = isGeneratingSuggestion || !hasHistory;

  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");

  useEffect(() => {
    const to = (recipientEmail && String(recipientEmail).trim()) || "";
    setEmailTo((prev) => (prev === "" && to ? to : prev || to));
  }, [recipientEmail]);

  useEffect(() => {
    if (!isExpanded) return;
    const sub = getDefaultReplySubject(messages);
    if (sub && !emailSubject) setEmailSubject("Re: " + sub);
  }, [isExpanded, messages, emailSubject]);

  const handleExpand = () => setIsExpanded(true);
  const handleCollapse = () => setIsExpanded(false);

  const onSend = () => {
    const hasContent = composerHasSendPayload(message, file, files, null);
    const to = emailTo.trim();
    if (hasContent && to && typeof handleSendMessage === "function") {
      handleSendMessage({
        emailTo: to,
        emailCc: emailCc.trim() ? emailCc.trim().split(/\s*[,;]\s*/).filter(Boolean) : undefined,
        emailSubject: emailSubject.trim() || undefined,
      });
      handleCollapse();
    }
  };

  const stripHtml = (html) => {
    const tempDiv = document.createElement("div");
    // Substitui <br> e <div> por quebras de linha
    html = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/div>/gi, "\n");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };  

  return (
    <Box
        sx={{
        position: "relative", 
        width: "100%",
        display: "flex",
        flexDirection: "column",
        // Mantém alinhado com os demais canais (WhatsApp/SMS/etc).
        paddingBottom: 0,
        }}
    >
      {!isExpanded ? (
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "stretch" : "center"}
          gap={isMobile ? 0.5 : 1}
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "stretch",
              borderRadius: `${inputBorderRadius}px`,
              overflow: "hidden",
              ...composerSurface.shell,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                alignSelf: "stretch",
                px: 0.25,
                ...composerSurface.divider,
              }}
            >
              <ChannelSelector integrated selectedChannel={selectedChannel} setSelectedChannel={setSelectedChannel} />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              variant="outlined"
              placeholder={`Envie uma mensagem via ${channelType}`}
              value={stripHtml(message)}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  const to = (emailTo.trim() || String(recipientEmail || "").trim());
                  const hasContent = composerHasSendPayload(message, file, files, null);
                  if (!to || !hasContent) return;
                  handleSendMessage({
                    emailTo: to,
                    emailCc: emailCc.trim()
                      ? emailCc.trim().split(/\s*[,;]\s*/).filter(Boolean)
                      : undefined,
                    emailSubject: emailSubject.trim() || undefined,
                  });
                }
              }}
              InputProps={{
                style: {
                  borderRadius: 0,
                  fontSize: isMobile ? "0.95rem" : "1rem",
                },
                inputRef: inputRef,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <IconButton onClick={handleEmojiPickerOpen} edge="start" aria-label="Emoji">
                      <InsertEmoticonIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}>
                    {canUseAiSuggestion && (
                      <Tooltip
                        title={
                          !hasHistory
                            ? "Disponível após a conversa ter histórico."
                            : isGeneratingSuggestion
                            ? "Gerando resposta..."
                            : "Sugerir resposta com IA"
                        }
                      >
                        <span>
                          <IconButton onClick={onGenerateSuggestion} disabled={aiDisabled}>
                            <AutoAwesome />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <ComposeAttachMenu openFilePicker={openFilePicker} showAttachProducts={false} showLocation={false} size="medium" />
                    <IconButton onClick={handleExpand}>
                      <ExpandIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        const to = (emailTo.trim() || String(recipientEmail || "").trim());
                        const hasContent = composerHasSendPayload(message, file, files, null);
                        if (!to || !hasContent) return;
                        handleSendMessage({
                          emailTo: to,
                          emailCc: emailCc.trim()
                            ? emailCc.trim().split(/\s*[,;]\s*/).filter(Boolean)
                            : undefined,
                          emailSubject: emailSubject.trim() || undefined,
                        });
                      }}
                      disabled={
                        !(emailTo.trim() || String(recipientEmail || "").trim()) ||
                        !composerHasSendPayload(message, file, files, null)
                      }
                      aria-label="Enviar"
                      sx={{
                        ml: 0.25,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        borderRadius: "50%",
                        p: 1,
                        "&:hover": { backgroundColor: "primary.dark" },
                        "&.Mui-disabled": {
                          backgroundColor: "action.disabledBackground",
                          color: "action.disabled",
                        },
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-root": {
                  minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
                  boxSizing: "border-box",
                  borderRadius: "0 !important",
                  overflow: "hidden",
                  clipPath: "none",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0 !important",
                  overflow: "hidden",
                  clipPath: "none",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                  borderRadius: "0 !important",
                },
                "& textarea": {
                  overflowY: "auto",
                  resize: "none",
                  boxSizing: "border-box",
                  borderRadius: "inherit",
                  overflowX: "hidden",
                  padding: "4px 10px",
                },
              }}
            />
          </Box>

        {/* Mobile: o próprio TextField já contém os botões no endAdornment. */}
        </Box>
      ) : (
        <Modal open={isExpanded} onClose={handleCollapse}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : "80%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Nova Mensagem</Typography>
          <IconButton onClick={handleCollapse} size="small">
            <CollapseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Para"
            placeholder="destinatário@email.com"
            variant="standard"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mb: 1, borderBottom: "1px solid #ddd" }}
          />
          <TextField
            fullWidth
            label="CC"
            placeholder="opcional, separar por vírgula"
            variant="standard"
            value={emailCc}
            onChange={(e) => setEmailCc(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mb: 1, borderBottom: "1px solid #ddd" }}
          />
          <TextField
            fullWidth
            label="Assunto"
            placeholder="Assunto do e-mail"
            variant="standard"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mb: 2, borderBottom: "1px solid #ddd" }}
          />
        </Box>


        <ReactQuill
          value={message}
          onChange={setMessage}
          theme="snow"
          placeholder="Escreva sua mensagem aqui..."
          style={{
            flex: 1,             // Permite que o editor cresça para ocupar o espaço disponível
            minHeight: "200px",   // Altura mínima
            maxHeight: "300px",   // Altura máxima
            overflowY: "auto",    // Barra de rolagem vertical se o conteúdo exceder a altura
        }}
        />
        
        {(files.length ? files : file ? [file] : []).map((f, index) => (
          <FilePreview
            key={`${f?.name}-${f?.size}-${index}`}
            file={f}
            onRemove={() => handleRemoveFile(index)}
          />
        ))}

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
        <IconButton onClick={openFilePicker}>
            <AttachFileIcon />
        </IconButton>
          <Button
            variant="outlined"
            onClick={handleCollapse}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSend}
            disabled={!composerHasSendPayload(message, file, files, null) || !emailTo.trim()}
            sx={{ textTransform: "none" }}
          >
            Enviar
          </Button>
        </Box>
      </Box>
    </Modal>
      )}
    </Box>
  );
};

export default MessageEmailChannel;
